import { datadogRum } from '@datadog/browser-rum'
import { COOKIE_KEYS } from 'constants/common'
import {
  AuthContext,
  loginUserAction,
  logoutUserAction,
  setAnonymousAction,
} from 'context/AuthContext'
import { TWPERMISSIONS } from 'dataSource/auth/types'
import UserContext from 'context/common/User/UserContext'
import {
  JwtResponseDto,
  loginWithMagicLink,
  loginWithOTP,
  verifyWithOTP,
} from 'dataSource/auth'
import Cookies from 'js-cookie'
import { useContext, useEffect } from 'react'
import {
  CustomToken,
  getPermissionFromToken,
  getUserPermissions,
} from './helpers'
import { useRouter } from 'next/navigation'
import jwtDecode from 'jwt-decode'
import {
  EmailOTPResponse,
  MagicTokenResponse,
  RedirectLoginOptions,
  UseThriveAuthResult,
} from './types'
import { resetAcceptedMedicaidSession } from 'app/Bookings/utils'
import { clearStoredPartnershipCode } from 'utils/b2bDetails'
import { getSafeReturnTo } from 'utils/auth'
import {
  clearSessionStorageClinicianData,
  clearSupeAdminCookies,
} from 'utils/thrivecare'
import { clearAllRecentSearches } from '@app/coe-dashboard/shared/utils/search'

const useThriveAuth = (): UseThriveAuthResult => {
  const router = useRouter()
  const [authState, dispatch] = useContext(AuthContext)
  const { clearUser, ...userContext } = useContext(UserContext)

  const loginWithRedirect = (options?: RedirectLoginOptions) => {
    if (options?.clearCredentials) {
      clearCredentials()
    }

    if (typeof options?.appState?.returnTo === 'string') {
      options.appState.returnTo =
        getSafeReturnTo(options.appState.returnTo) || ''
    }

    const query = new URLSearchParams(
      options?.appState as Record<string, string>
    )
    const queryString = query.toString() ? `?${query.toString()}` : ''
    router.replace(`/login${queryString}`)
  }

  const loginUser = (permissions: TWPERMISSIONS[]) =>
    dispatch(loginUserAction(permissions))

  const logoutUser = () => dispatch(logoutUserAction())

  const clearCredentials = () => {
    localStorage.removeItem('access_token')
    resetAcceptedMedicaidSession()
    clearStoredPartnershipCode()
    // COE dashboard recent searches are patient names/emails/phones in
    // localStorage; they must not survive sign-out on a shared workstation.
    clearAllRecentSearches()
    clearUser()
    datadogRum.clearUser()
    Cookies.remove(COOKIE_KEYS.USER)
    Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN)
    // Drop any super-admin impersonation so clearing credentials never leaves
    // us still acting as an impersonated clinician.
    clearSupeAdminCookies()
    clearSessionStorageClinicianData()
    logoutUser()
  }

  const logout = async (options: { returnTo?: string }) => {
    try {
      const { returnTo } = options
      clearCredentials()

      const safeReturnTo = getSafeReturnTo(returnTo)

      if (safeReturnTo) {
        await router.replace(safeReturnTo)
      }
    } catch (error) {}
  }

  const setAnonymous = () => {
    dispatch(setAnonymousAction())
  }

  useEffect(() => {
    handleAuthentication({})
  }, [])

  const handleAuthentication = (
    props: JwtResponseDto
  ): TWPERMISSIONS[] | null => {
    // check jwt by getting user information
    const localToken = getAccessTokenSilently()
    const { access_token } = props
    const token = access_token || localToken

    if (token) {
      // A fresh login supplies a NEW access_token (magic-link / OTP). That's a
      // new identity, so drop any super-admin impersonation left over from the
      // previous session. Guard on `access_token` specifically — the silent
      // re-auth on mount passes {} (token comes from localStorage), and we must
      // NOT clear impersonation there or we'd wipe it on every page load.
      if (access_token) {
        clearSupeAdminCookies()
        clearSessionStorageClinicianData()
      }

      localStorage.setItem('access_token', token)

      try {
        const userObject: CustomToken = jwtDecode(token)

        Cookies.set(COOKIE_KEYS.ACCESS_TOKEN, token, {
          ...(userObject?.exp
            ? { expires: new Date(userObject.exp * 1000) }
            : {}),
          sameSite: process.env.NODE_ENV === 'development' ? 'Lax' : 'None',
          secure: process.env.NODE_ENV !== 'development',
        })
      } catch {
        // Invalid token — still proceed with localStorage for existing behavior
      }

      const permissions = getPermissionFromToken(token)
      loginUser(permissions)
      return permissions
    } else {
      setAnonymous()
    }

    return null
  }

  const requestMagicLink = async (
    email: string,
    params: {
      redirectTo: string
    }
  ): Promise<MagicTokenResponse> => {
    const result = (await loginWithMagicLink(
      email,
      params
    )) as MagicTokenResponse
    return result
  }

  const requestOTP = async (
    email: string,
    isRetry?: boolean
  ): Promise<EmailOTPResponse> => {
    const result = (await loginWithOTP(email, isRetry)) as EmailOTPResponse
    return result
  }

  const verifyOTP = async (
    email: string,
    otp: string
  ): Promise<JwtResponseDto> => {
    const authResponse = await verifyWithOTP(email, otp)
    return authResponse
  }

  const getAccessTokenSilently = () => {
    return localStorage.getItem('access_token')
  }

  const userPermissions = getUserPermissions(authState.permissions)

  return {
    ...authState,
    ...userContext,
    ...userPermissions,
    clearUser,

    getAccessTokenSilently,

    loginWithRedirect,
    logout,
    requestMagicLink,
    requestOTP,
    verifyOTP,
    handleAuthentication,
  }
}

export default useThriveAuth
